import { useState, useEffect, useRef, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useNavigate, useParams } from 'react-router-dom';
import { getCommentList, pollCommentList, addComment, likeComment } from '../api/liveCommentService';
import { getUserInfo, getStorage, STORAGE_KEYS } from '@shared/api/client';
import { getMyProfile } from '@features/profile/api/profileService';
import { ROUTES } from '@app/router/routes';
import { createPortal } from 'react-dom';
import CommenterNameSelect, { useNameOptions } from '@shared/components/CommenterNameSelect';
import LiveBadge from '@shared/components/LiveBadge';
import { useLanguage } from '@shared/contexts/LanguageContext';

const POLL_INTERVAL = 20000;
const MAX_LENGTH = 300;

function getLikedStorageKey(userId, topicId) {
  return `liked_comments_user${userId}_topic${topicId}`;
}
function loadLikedFromStorage(userId, topicId) {
  try {
    const raw = localStorage.getItem(getLikedStorageKey(userId, topicId));
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
}
function saveLikedToStorage(userId, topicId, likedSet) {
  try {
    localStorage.setItem(getLikedStorageKey(userId, topicId), JSON.stringify([...likedSet]));
  } catch { }
}

function isBoardActive(board) {
  if (!board || board.settled) return false;
  const now = Date.now();
  return now >= new Date(board.start_time).getTime() && now <= new Date(board.end_time).getTime();
}

function isBoardEnded(board) {
  if (!board || board.settled) return false;
  const endTime = new Date(board.end_time).getTime();
  return Number.isFinite(endTime) && Date.now() > endTime;
}

function formatDateTime(isoString) {
  const d = new Date(isoString);
  return d.toLocaleString('zh-TW', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function getDisplayName(comment) {
  if (comment.commenter_name) return comment.commenter_name;
  const name = (comment.dharma_name && comment.dharma_name !== '無') ? comment.dharma_name : comment.user_name;
  return comment.monastery_name ? `${comment.monastery_name}・${name}` : name;
}

function CommentCard({ comment, liked, onLike, likeEnabled = true }) {
  const { t } = useLanguage();
  return (
    <div className="flex gap-3 px-4 py-2.5 border-b border-[#F5F1EB] last:border-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold text-[--color-primary]">{getDisplayName(comment)}</span>
          <span className="text-xs text-[--color-text-sub] shrink-0">{formatDateTime(comment.created_at)}</span>
        </div>
        <p className="text-sm text-[--color-text-main] leading-relaxed mt-1 break-words">{comment.content}</p>
      </div>
      <div
        onClick={likeEnabled && !liked ? () => onLike(comment.comment_id) : undefined}
        aria-label={t('live-comment-topic.likeAria')}
        className={`flex flex-col items-center gap-0.5 shrink-0 min-w-[44px] min-h-[44px] justify-center ${likeEnabled && !liked ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <span
          className={`material-symbols-outlined transition-colors duration-200 ${liked ? 'text-red-400' : likeEnabled ? 'text-[--color-buddha-sand] hover:text-red-400' : 'text-[--color-buddha-sand]'}`}
          style={{ fontVariationSettings: liked ? "'FILL' 1" : "'FILL' 0", fontSize: '22px' }}
        >
          favorite
        </span>
        <span className="text-xs text-[--color-text-sub]">{comment.like_count}</span>
      </div>
    </div>
  );
}

function TopCommentCard({ comment, liked, onLike, likeEnabled = true }) {
  const { t } = useLanguage();
  return (
    <div className="flex gap-3 px-4 py-2.5 border-b border-[#EDE4D3] last:border-0 bg-[#FBF6EC]">
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold text-[--color-primary]">{getDisplayName(comment)}</span>
          <span className="text-xs text-[--color-text-sub] shrink-0">{formatDateTime(comment.created_at)}</span>
        </div>
        <p className="text-sm text-[--color-text-main] leading-relaxed mt-1 break-words">{comment.content}</p>
      </div>
      <div
        onClick={likeEnabled && !liked ? () => onLike(comment.comment_id) : undefined}
        aria-label={t('live-comment-topic.likeAria')}
        className={`flex flex-col items-center gap-0.5 shrink-0 min-w-[40px] min-h-[40px] justify-center ${likeEnabled && !liked ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <span
          className={`material-symbols-outlined transition-colors duration-200 text-[20px] ${liked ? 'text-red-400' : likeEnabled ? 'text-[--color-buddha-sand] hover:text-red-400' : 'text-[--color-buddha-sand]'}`}
          style={{ fontVariationSettings: liked ? "'FILL' 1" : "'FILL' 0" }}
        >
          favorite
        </span>
        <span className="text-xs text-[--color-text-sub]">{comment.like_count}</span>
      </div>
    </div>
  );
}

function TopCommentsPopup({ topComments, likedIds, onLike, likeEnabled, onClose }) {
  const { t } = useLanguage();
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full md:max-w-2xl md:mx-auto md:w-[65%] bg-white rounded-t-2xl max-h-[70dvh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-[#E8DCC8] shrink-0">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-[--color-accent]" style={{ fontSize: '16px', fontVariationSettings: "'FILL' 1" }}>workspace_premium</span>
            <span className="text-sm font-semibold text-[--color-accent]">{t('live-comment-topic.hotPopupTitle')}</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5EFE6] transition-colors cursor-pointer">
            <span className="material-symbols-outlined text-[--color-text-sub]" style={{ fontSize: '20px' }}>close</span>
          </button>
        </div>
        <div className="overflow-y-auto flex-1">
          {topComments.length === 0
            ? <p className="text-center text-sm text-[--color-text-sub] py-10">{t('live-comment-topic.hotEmpty')}</p>
            : topComments.map(comment => (
              <TopCommentCard
                key={`popup-top-${comment.comment_id}`}
                comment={comment}
                liked={likedIds.has(comment.comment_id)}
                onLike={onLike}
                likeEnabled={likeEnabled}
              />
            ))
          }
        </div>
      </div>
    </div>,
    document.body
  );
}

function EmptyState() {
  const { t } = useLanguage();
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-20 text-center px-8">
      <div className="w-16 h-16 rounded-full bg-[#F5EFE6] flex items-center justify-center">
        <span
          className="material-symbols-outlined text-[--color-buddha-sand]"
          style={{ fontVariationSettings: "'FILL' 1", fontSize: '32px' }}
        >
          chat_bubble
        </span>
      </div>
      <div>
        <p className="text-sm font-medium text-[--color-text-main]">{t('live-comment-topic.emptyTitle')}</p>
        <p className="text-xs text-[--color-text-sub] mt-1">{t('live-comment-topic.emptyHint')}</p>
      </div>
    </div>
  );
}

export default function CommentBoardPage() {
  const { boardId, topicId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const user = getUserInfo();
  const isLoggedIn = !!getStorage(STORAGE_KEYS.MEMBER_TOKEN);

  const [topicTitle, setTopicTitle] = useState('');
  const [boardDetails, setBoardDetails] = useState(null);
  const [comments, setComments] = useState([]);
  const [topComments, setTopComments] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [likedIds, setLikedIds] = useState(new Set());
  const [input, setInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [profileName, setProfileName] = useState(user?.name ?? '');
  const [profileDharma, setProfileDharma] = useState(user?.dharmaName ?? '');
  const [profileTemple, setProfileTemple] = useState(user?.monasteryName ?? '');
  const nameOptions = useNameOptions({ name: profileName, dharma: profileDharma, temple: profileTemple });
  const SELECTED_NAME_KEY = `commenter_name_user${user?.id ?? 'guest'}`;
  const [selectedName, setSelectedName] = useState(() => localStorage.getItem(SELECTED_NAME_KEY) ?? '');
  const [sortMode, setSortMode] = useState(1); // 1=最新 2=讚數最多
  const [topPopupOpen, setTopPopupOpen] = useState(false);
  const [newCount, setNewCount] = useState(0);
  const [refreshToast, setRefreshToast] = useState(''); // 'none' | 'added:N' | ''
  const refreshToastTimerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const pollIntervalRef = useRef(POLL_INTERVAL);
  const sortModeRef = useRef(1);
  const afterIdRef = useRef(null);
  const existingIdsRef = useRef(new Set());
  const maxCommentIdRef = useRef(0);
  const listRef = useRef(null);
  const textareaRef = useRef(null);
  const isAtTopRef = useRef(true);

  const virtualizer = useVirtualizer({
    count: comments.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => 80,
    measureElement: (el) => el?.getBoundingClientRect().height ?? 80,
    overscan: 5,
  });

  const boardEnded = isBoardEnded(boardDetails);
  const boardSettled = Boolean(boardDetails?.settled);
  const commentClosed = boardEnded || boardSettled || boardDetails?.comment_switch === false;
  const likeEnabled = boardDetails?.like_switch !== false && !commentClosed;


  useEffect(() => {
    if (!isLoggedIn) return;
    getMyProfile().then(profile => {
      if (!profile) return;
      setProfileName(profile.user_name ?? user?.name ?? '');
      setProfileDharma(profile.dharma_name ?? user?.dharmaName ?? '');
      setProfileTemple(profile.monastery_name ?? user?.monasteryName ?? '');
    }).catch(() => { });
  }, [isLoggedIn]);

  useEffect(() => {
    if (nameOptions.length > 0 && !nameOptions.find(o => o.value === selectedName)) {
      const saved = localStorage.getItem(SELECTED_NAME_KEY);
      const fallback = nameOptions.find(o => o.value === saved) ? saved : nameOptions[0].value;
      setSelectedName(fallback);
    }
  }, [nameOptions]);

  const handleSelectName = (name) => {
    setSelectedName(name);
    localStorage.setItem(SELECTED_NAME_KEY, name);
  };

  const loadComments = useCallback(async () => {
    const res = await getCommentList(topicId, { sort: 1 });
    if (res.success) {
      const { items = [], top_items = [], topic_details } = res.data;
      if (topic_details?.title) setTopicTitle(topic_details.title);
      const userId = user?.id ?? 'guest';
      const likedFromApi = new Set([
        ...items.filter(c => c.is_liked).map(c => c.comment_id),
        ...top_items.filter(c => c.is_liked).map(c => c.comment_id),
      ]);
      saveLikedToStorage(userId, topicId, likedFromApi);
      setLikedIds(likedFromApi);
    }
  }, [topicId, user?.id]);

  useEffect(() => { loadComments().then(() => poll(sortMode)); }, [loadComments]);

  const showRefreshToast = useCallback((msg) => {
    setRefreshToast(msg);
    if (refreshToastTimerRef.current) clearTimeout(refreshToastTimerRef.current);
    refreshToastTimerRef.current = setTimeout(() => setRefreshToast(''), 2500);
  }, []);

  const poll = useCallback(async (sort = 1, isBackground = false, isManual = false) => {
    const res = await pollCommentList(topicId, { sort });
    if (res.success) {
      const { items = [], board_comment_switch, board_end_time, board_settled, board_start_time, polling_seconds } = res.data;
      setBoardDetails({ comment_switch: board_comment_switch, end_time: board_end_time, settled: board_settled, start_time: board_start_time });
      if (polling_seconds) pollIntervalRef.current = polling_seconds * 1000;
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = setInterval(() => poll(sortModeRef.current, true), pollIntervalRef.current);
      if (sort === 2) items.sort((a, b) => b.like_count - a.like_count);
      if (isBackground && sort === 1) {
        setComments(prev => {
          const added = items.length - prev.length;
          if (added > 0 && !isAtTopRef.current) {
            setNewCount(c => c + added);
          }
          return items;
        });
      } else {
        setComments(prev => {
          if (isManual) {
            const added = items.length - prev.length;
            if (added > 0) showRefreshToast(t('live-comment-topic.refreshAdded').replace('{{count}}', added));
            else showRefreshToast(t('live-comment-topic.refreshNone'));
          }
          return items;
        });
      }
      setTotalItems(items.length);
    }
  }, [topicId, showRefreshToast]);

  useEffect(() => {
    if (commentClosed) clearInterval(pollTimerRef.current);
    return () => clearInterval(pollTimerRef.current);
  }, [commentClosed]);

  const scrollToTop = useCallback(() => {
    virtualizer.scrollToIndex(0);
    setNewCount(0);
  }, [virtualizer]);

  const handleListScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    isAtTopRef.current = el.scrollTop < 60;
    if (isAtTopRef.current) setNewCount(0);
  }, []);

  useEffect(() => {
    // 最新模式在頂部時自動 scroll 到頂顯示新留言，讚數模式不動
    if (sortMode === 1 && isAtTopRef.current) scrollToTop();
  }, [comments, sortMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || !selectedName || submitting || commentClosed) return;
    setSubmitting(true);
    setSubmitError('');
    setInput('');
    const res = await addComment(topicId, text, selectedName);
    if (res.success) {
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 3000);
    } else {
      setInput(text); // 送出失敗悄復原內容
      const msg = res.result_code === 931 ? t('live-comment-topic.duplicateError') : t('live-comment-topic.submitFailed');
      setSubmitError(msg);
      setTimeout(() => setSubmitError(''), 4000);
    }
    setSubmitting(false);
  };

  const handleLike = async (commentId) => {
    if (likedIds.has(commentId)) return;
    const userId = user?.id ?? 'guest';
    const nextLiked = new Set([...likedIds, commentId]);
    setLikedIds(nextLiked);
    const bump = arr => arr.map(c => c.comment_id === commentId ? { ...c, like_count: c.like_count + 1 } : c);
    setComments(bump);
    setTopComments(bump);
    const res = await likeComment(commentId);
    if (res.success) {
      saveLikedToStorage(userId, topicId, nextLiked);
    } else {
      const rollback = new Set(nextLiked);
      rollback.delete(commentId);
      setLikedIds(rollback);
      const unbump = arr => arr.map(c => c.comment_id === commentId ? { ...c, like_count: c.like_count - 1 } : c);
      setComments(unbump);
      setTopComments(unbump);
    }
  };


  return (
    <div className="flex flex-col h-[calc(100dvh-120px)] bg-[#FAF7F3] md:max-w-2xl md:mx-auto md:w-[65%]">

      {/* 頂部 */}
      <div className="bg-gradient-to-b from-[#FAF7F3] to-[#F5EFE6] border-b border-[#E8DCC8] px-4 pb-4 -mt-[70px] pt-[calc(70px+0.75rem)] md:-mt-[86px] md:pt-[calc(86px+0.75rem)]">
        <div className="flex items-center justify-between gap-3 mb-3">
          <button
            onClick={() => navigate(ROUTES.LIVE_COMMENT_BOARD(boardId))}
            className="inline-flex items-center gap-1.5 text-sm text-[--color-text-sub] hover:text-[--color-primary] bg-white/70 hover:bg-white px-3 py-1.5 rounded-full transition-all duration-200 cursor-pointer shadow-sm"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>arrow_back_ios</span>
            {t('live-comment-topic.backToSessions')}
          </button>
          {false && comments.some(c => c.rank !== null) && (
            <button
              onClick={() => setTopPopupOpen(true)}
              className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/70 transition-colors duration-200 text-orange-400"
              aria-label={t('live-comment-topic.hotCommentsAria')}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '22px', fontVariationSettings: "'FILL' 1" }}>local_fire_department</span>
            </button>
          )}
        </div>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-[--color-text-main] leading-snug">{topicTitle}</h2>
            {boardDetails?.settled && (
              <LiveBadge type="settled" className="mt-1" />
            )}
            {isBoardEnded(boardDetails) && (
              <LiveBadge type="ended" className="mt-1" />
            )}
            {isBoardActive(boardDetails) && (
              <LiveBadge type="live" className="mt-1" />
            )}
          </div>
          {!boardEnded && !boardSettled && (
            <button
              onClick={() => poll(sortMode, false, true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#E8DCC8] bg-white text-xs text-[--color-text-sub] hover:border-[--color-primary] hover:text-[--color-primary] transition-all duration-200 cursor-pointer"
              aria-label={t('live-comment-topic.reloadAria')}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>refresh</span>
            </button>
          )}
        </div>
        <div className="flex gap-2 mt-2">
          {[{ value: 1, label: t('live-comment-topic.sortLatest') }, { value: 2, label: t('live-comment-topic.sortMostLiked') }].map(opt => (
            <button
              key={opt.value}
              onClick={() => {
                sortModeRef.current = opt.value;
                setSortMode(opt.value);
                setNewCount(0);
                poll(opt.value).then(() => {
                  if (opt.value === 1) {
                    isAtTopRef.current = true;
                    setTimeout(() => scrollToTop(), 0);
                  } else {
                    isAtTopRef.current = false;
                    if (listRef.current) listRef.current.scrollTop = 0;
                  }
                });
              }}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer border-2 ${sortMode === opt.value ? 'bg-[#907654] border-[#907654] text-white shadow-md' : 'bg-white border-[#E8DCC8] text-[#6E625B] hover:border-[#907654] hover:text-[#907654]'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* 留言列表（熱門留言混入一般留言） */}
      <div className="relative flex-1 min-h-0">
        {refreshToast && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 px-4 py-2 bg-[#907654] text-white text-xs font-semibold rounded-full shadow-lg pointer-events-none whitespace-nowrap">
            {refreshToast}
          </div>
        )}
        {false && newCount > 0 && (
          <button
            onClick={scrollToTop}
            className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 px-3 py-1.5 bg-[#907654] text-white text-xs font-semibold rounded-full shadow-lg cursor-pointer hover:bg-[#7a6244] transition-colors duration-200"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>arrow_downward</span>
            {t('live-comment-topic.newComments').replace('{{count}}', newCount)}
          </button>
        )}
        <div ref={listRef} onScroll={handleListScroll} className="h-full overflow-y-auto bg-white">
          {comments.length === 0
            ? <EmptyState />
            : (
              <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
                {virtualizer.getVirtualItems().map((virtualItem) => {
                  const comment = comments[virtualItem.index];
                  const isTop = comment.rank !== null && comment.rank !== undefined;
                  const Card = isTop ? TopCommentCard : CommentCard;
                  return (
                    <div
                      key={comment.comment_id}
                      data-index={virtualItem.index}
                      ref={virtualizer.measureElement}
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', transform: `translateY(${virtualItem.start}px)` }}
                    >
                      <Card
                        comment={comment}
                        liked={likedIds.has(comment.comment_id)}
                        onLike={handleLike}
                        likeEnabled={likeEnabled}
                      />
                    </div>
                  );
                })}
              </div>
            )
          }
        </div>
      </div>

      {/* 送出成功 / 失敗提示 */}
      {submitted && (
        <div className="px-4 py-2.5 bg-[#F5EFE6] border-t border-[#E8DCC8] text-xs text-[--color-text-sub] text-center">
          {t('live-comment-topic.submittedPending')}
        </div>
      )}
      {submitError && (
        <div className="px-4 py-2.5 bg-red-50 border-t border-red-100 text-xs text-red-500 text-center">
          {submitError}
        </div>
      )}

      {/* 輸入列 / 未登入提示 */}
      {isLoggedIn && (boardSettled || boardDetails?.comment_switch === false) ? (
        <div className="px-4 py-3 bg-white border-t border-[#E8DCC8] text-xs text-[--color-text-sub] text-center">
          {t('live-comment-topic.boardPaused')}
        </div>
      ) : isLoggedIn && boardEnded ? (
        <div className="px-4 py-3 bg-white border-t border-[#E8DCC8] text-xs text-[--color-text-sub] text-center">
          {t('live-comment-topic.boardEnded')}
        </div>
      ) : isLoggedIn ? (
        <form onSubmit={handleSubmit} className="px-4 pt-3 pb-4 bg-white border-t border-[#E8DCC8]">
          <div className="mb-2">
            <CommenterNameSelect value={selectedName} options={nameOptions} onChange={handleSelectName} />
          </div>
          <div className="flex gap-2 items-end">
            <div className="flex-1 flex flex-col gap-1">
              <textarea
                ref={textareaRef}
                rows={1}
                value={input}
                onChange={e => {
                  setInput(e.target.value);
                  const el = textareaRef.current;
                  if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; }
                }}
                placeholder={t('live-comment-topic.placeholder')}
                maxLength={MAX_LENGTH}
                className="w-full bg-[#FAF7F3] rounded-xl px-4 py-2.5 text-sm text-[--color-text-main] placeholder:text-stone-300 focus:outline-none focus:ring-1 focus:ring-[--color-primary] resize-none overflow-hidden"
              />
              <span className={`text-xs tabular-nums text-right pr-1 ${input.length >= MAX_LENGTH ? 'text-red-400' : 'text-stone-300'}`}>
                {input.length}/{MAX_LENGTH}
              </span>
            </div>
            <button
              type="submit"
              disabled={!input.trim() || !selectedName || submitting}
              className="shrink-0 px-5 py-2.5 bg-[#907654] text-white rounded-xl text-sm font-medium disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed transition-opacity duration-200 self-start mt-0"
            >
              {submitting ? '…' : t('live-comment-topic.submit')}
            </button>
          </div>
        </form>
      ) : (
        <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-[#E8DCC8]">
          <span className="text-sm text-[--color-text-sub]">{t('live-comment-topic.loginToComment')}</span>
          <button
            onClick={() => navigate(ROUTES.LOGIN)}
            className="px-4 py-2 bg-[--color-primary] text-white rounded-xl text-sm font-medium cursor-pointer"
          >
            {t('live-comment-topic.goLogin')}
          </button>
        </div>
      )}

      {topPopupOpen && (
        <TopCommentsPopup
          topComments={comments.filter(c => c.rank !== null && c.rank !== undefined).sort((a, b) => a.rank - b.rank)}
          likedIds={likedIds}
          onLike={handleLike}
          likeEnabled={likeEnabled}
          onClose={() => setTopPopupOpen(false)}
        />
      )}
    </div>
  );
}
